import { action, set } from "@ember/object";
import { service } from "@ember/service";
import { tracked } from "@glimmer/tracking";
import type { EmberChangeset } from "ember-changeset";
import { Changeset } from "ember-changeset";
import type { ForcBaseArgs } from "parog-web/components/forc/abstract/base-component";
import type { ForcModelPojo } from "parog-web/components/forc/abstract/journal-event-based-component";
import JournalEventBasedComponent from "parog-web/components/forc/abstract/journal-event-based-component";
import type ComorbiditiesAndAllergiesChangedJournalEvent from "parog-web/models/comorbidities-and-allergies-changed-journal-event";
import type JournalEventModel from "parog-web/models/journal-event";
import ParogMedicalHistoryChangedJournalEvent from "parog-web/models/medical-history-changed-journal-event";
import type Patient from "parog-web/models/patient";
import type TreatmentPrescriptionChangedJournalEvent from "parog-web/models/treatment-prescription-changed-journal-event";
import Vaccine from "parog-web/models/vaccine";
import type ConfigurationService from "parog-web/services/configuration";
import type FetcherService from "parog-web/services/fetcher";

interface Args extends ForcBaseArgs {
  patient: Patient;
}

/**
 * This component contains the shared logic about the medical history.
 * So that when defining the edition form for a parog, we just need to setup
 * the `journalEventProperties` array, containing the `sharedProperties` and
 * the parog specfifc ones.
 */
export default class BaseMedicalHistoryFormComponent extends JournalEventBasedComponent<Args> {
  // ABSTRACT
  static fullComponentId = "medical-history/edition-form";
  static forcDisplayName = "Synthèse médicale";

  @service declare configuration: ConfigurationService;
  @service declare fetcher: FetcherService;

  @tracked
  vaccinesAreDirty;

  sharedProperties = ["occurredAt", "vaccines", "comments"];
  static journalEventClass = ParogMedicalHistoryChangedJournalEvent;
  retrieveJournalEvent = () =>
    this.args.patient.medicalHistoryChangedJournalEvent;

  retrieveModel(): ForcModelPojo {
    const model = super.retrieveModel();

    if (this.isNewMode) {
      model.occurredAt = new Date();
    }

    if (!model.vaccines) {
      model.vaccines = [
        new Vaccine("pneumococcus"),
        new Vaccine("dtPolio"),
        new Vaccine("yellowFever"),
        new Vaccine("seasonalFlu"),
        new Vaccine("sarsCov2")
      ];
    }

    return model;
  }

  get comorbiditiesModel(): ComorbiditiesAndAllergiesChangedJournalEvent {
    const lastComorbiditiesJe =
      this.patient.comorbiditiesAndAllergiesChangedJournalEvent;

    if (!lastComorbiditiesJe) {
      return this.store.createRecord(
        "comorbidities-and-allergies-changed-journal-event",
        {
          occurredAt: new Date()
        }
      );
    }

    return lastComorbiditiesJe;
  }

  get treatmentsModel(): TreatmentPrescriptionChangedJournalEvent {
    const lastTreatmentPrescriptionChangedJournalEvent =
      this.patient.lastTreatmentPrescriptionChangedJournalEvent;

    if (!lastTreatmentPrescriptionChangedJournalEvent) {
      return this.store.createRecord(
        "treatment-prescription-changed-journal-event",
        {
          occurredAt: new Date(),
          ignoredAtcCodes: [],
          atc: []
        }
      );
    }
    return lastTreatmentPrescriptionChangedJournalEvent;
  }

  get comorbiditiesChangeset(): EmberChangeset {
    const comorbiditiesChangeset = Changeset(this.comorbiditiesModel);

    Object.defineProperty(this, "comorbiditiesChangeset", {
      value: comorbiditiesChangeset,
      writable: false
    });

    return this.comorbiditiesChangeset;
  }

  get treatmentsChangeset(): EmberChangeset {
    const treatmentsChangeset = Changeset(this.treatmentsModel);

    Object.defineProperty(this, "treatmentsChangeset", {
      value: treatmentsChangeset,
      writable: false
    });

    return this.treatmentsChangeset;
  }

  get isDirty(): boolean {
    if (this.patient.medicalHistoryChangedJournalEvent) {
      return (
        this.vaccinesAreDirty ||
        this.comorbiditiesChangeset.isDirty ||
        this.treatmentsChangeset.isDirty ||
        super.isDirty
      );
    }
    return true;
  }

  @action
  setDoneProperty(targetVaccine: Vaccine, value: boolean): void {
    this.setVaccinePropertyInChangeset(targetVaccine, "done", value);
  }

  @action
  setVaccinationDateProperty(targetVaccine: Vaccine, value: Date): void {
    this.setVaccinePropertyInChangeset(targetVaccine, "vaccinationDate", value);
  }

  @action
  setVaccinePropertyInChangeset(
    targetVaccine: Vaccine,
    key: string,
    value: string | boolean | Date
  ): void {
    const vaccines = this.changeset.get("vaccines");
    const vaccine = vaccines.find(
      vaccine => vaccine.name == targetVaccine.name
    );
    set(vaccine, key, value);
    this.vaccinesAreDirty = true;
    this.changeset.set("vaccines", vaccines);
  }

  get shouldShowDateLabel(): boolean | undefined {
    const vaccines = this.changeset.get("vaccines");
    return vaccines?.some(vaccine => vaccine.done);
  }

  async saveModel(): Promise<JournalEventModel> {
    if (this.comorbiditiesChangeset.isDirty) {
      if (this.comorbiditiesModel.get("isNew")) {
        this.comorbiditiesChangeset.set("patient", this.patient);
      }

      await this.comorbiditiesChangeset.save();
    }

    if (this.treatmentsChangeset.isDirty) {
      if (!this.treatmentsChangeset.get("prescriptions")) {
        this.treatmentsChangeset.set("prescriptions", []);
      }
      this.treatmentsChangeset.set("patient", this.patient);
      await this.treatmentsChangeset.save();
    }

    const model = await super.saveModel();

    await this.configuration.fetchConfigurationsForPatient(this.patient);

    const bioExamFqcyJes = await this.fetcher.loadRecords(
      "bio-exam-frequency-changed-journal-event",
      { filter: { patient_id: this.patient.id } }
    );

    await this.fetcher.loadRecords(
      "medical-act-frequency-changed-journal-event",
      { filter: { patient_id: this.patient.id } }
    );

    await Promise.all(
      bioExamFqcyJes.map(je => {
        return this.fetcher.loadRecords("loinc", {
          filter: { loinc: je.loincs },
          include: "bio-exam-order"
        });
      })
    );

    return model;
  }
}
